// 대균열(Greater Rift, PROGRESSION-720H 4절 · REBIRTH-MODES-SPEC 3.5) — 순수 규칙. 게임(game/field/rift.ts)·시뮬(tools/sim/pacing720.ts)·UI가 같은 값을 쓴다.
// 디아블로 대균열 패턴: 단계 하나하나가 새 도전이고, 15분 안에 수호자를 쓰러뜨리면 그 단계가 '최고 기록'이 되어 다음 단계가 열린다.
// 오를 수 있는 단계(상한)는 계정 전투력(환생 회차·지금 레벨·다른 영웅 레벨 합·숙련)이 정한다 — 장비·레벨이 받쳐 주지 않는 단계로 뛰어오르지 못하게.
// 하루 열쇠 2개(모아 두기 최대 6개, KST 자정 기준) = 시뮬의 하루 2회 도전(1회 15분)과 같은 결.

/** 최고 단계 */
export const GRIFT_MAX = 150;
/** 제한 시간(초) — 디아블로 대균열과 같은 15분 */
export const GRIFT_LIMIT_SEC = 900;
/** 하루에 받는 열쇠 수·모아 둘 수 있는 최대 */
export const GRIFT_KEYS_PER_DAY = 2;
export const GRIFT_KEY_STOCK = 6;
/** 빨리 깨면 한 번에 오르는 단계(남은 시간 비율 ≥ 문턱이면 +2, 그 밖 +1) — 디아블로의 '여유 있게 깨면 여러 단계 오름'을 상한 안에서만 */
export const GRIFT_FAST_SHARE = 0.5;

/**
 * 단계 상한(설계 식 그대로, PROGRESSION-720H 4절 — 시뮬 tools/sim/pacing720.ts riftCap이 이 함수를 그대로 쓴다): 1차 환생 뒤 열린다.
 * 상한 = 20 × (회차 − 1) + 지금 영웅 레벨/5 + 다른 영웅 레벨 합/10 + 숙련/2, 최대 150. 회차 0이면 0(잠김).
 * 1차 환생 직후 Lv1~4는 상한 0이라 입구가 일반 균열만 연다(대균열은 재육성 Lv5부터 — 시뮬 216.6h 1단계와 같은 시각).
 */
export function griftCap(cycle: number, heroLevel: number, altLevels: number, mastery: number): number {
  if (!(cycle >= 1)) return 0;
  const raw = 20 * (Math.floor(cycle) - 1) + Math.floor(Math.max(0, heroLevel) / 5) + Math.floor(Math.max(0, altLevels) / 10) + Math.floor(Math.max(0, mastery) / 2);
  return Math.max(0, Math.min(GRIFT_MAX, raw));
}

/** 고를 수 있는 가장 높은 단계 = min(상한, 최고 기록 + 1) */
export function griftSelectable(best: number, cap: number): number {
  return Math.max(cap > 0 ? 1 : 0, Math.min(cap, Math.max(0, best) + 1));
}

/** 단계 배율: 몹 체력·피해·수호자 체력. 단계당 체력 +9%, 피해 +4%(곱이 아닌 합 — 150단계 체력 ×14.4, 피해 ×6.96) */
export function griftHpMult(tier: number): number { return Math.round((1 + 0.09 * (clampTier(tier) - 1)) * 1000) / 1000; }
export function griftDamageMult(tier: number): number { return Math.round((1 + 0.04 * (clampTier(tier) - 1)) * 1000) / 1000; }
/** 수호자 체력은 몹보다 조금 더 가파르다(단계 체감의 정점) */
export function griftGuardianHpMult(tier: number): number { return Math.round((1.4 + 0.11 * (clampTier(tier) - 1)) * 1000) / 1000; }
/** 진행 게이지 점수: 일반 균열(240)보다 많다 — 단계가 오를수록 조금씩 더(15분을 쓰는 한 판) */
export function griftGaugeMax(tier: number): number { return 360 + 2 * (clampTier(tier) - 1); }
/** 층 수: 1~9단계 3층, 10단계부터 4층 */
export function griftFloors(tier: number): number { return clampTier(tier) >= 10 ? 4 : 3; }

/** 보상 배율(골드: 일반 균열 상자 기준 배수). 시간 안 클리어만 보상, 시간 초과는 위로 상자(골드 ×0.25, 단계 오르지 않음) */
export function griftGoldMult(tier: number): number { return Math.round((6 + 0.5 * (clampTier(tier) - 1)) * 100) / 100; }
export function griftStones(tier: number): number { return 6 + Math.floor(clampTier(tier) / 5); }
export const GRIFT_LATE_GOLD = 0.25;

/** 10단계마다 첫 돌파 이정표(대균열 기록 보상, 한 번만): 추가 강화석 */
export function griftMilestoneStones(tier: number): number { return tier > 0 && tier % 10 === 0 ? 10 + tier / 5 : 0; }

/** 클리어 뒤 새 최고 기록(상한 안). late면 기록 없음 */
export function griftNextBest(best: number, tier: number, cap: number, remainShare: number, late: boolean): number {
  if (late) return best;
  // 고른 단계 자체를 깬 것이 기록이고, 여유가 크면(남은 시간 ≥ 50%) 한 단계 더 열어 준다(상한 안에서만)
  const reach = tier + (remainShare >= GRIFT_FAST_SHARE ? 1 : 0);
  return Math.max(best, Math.min(cap, reach));
}

/** 열쇠: 마지막으로 열쇠를 받은 날(day) 이후 지난 날 × 2를 더하고 최대 6. 시계가 뒤로 가면 주지 않는다 */
export function griftKeysAt(keys: number, lastDay: number, day: number): { keys: number; day: number } {
  if (!(day > lastDay)) return { keys: Math.max(0, Math.min(GRIFT_KEY_STOCK, keys)), day: Math.max(lastDay, day) };
  const add = Math.min(GRIFT_KEY_STOCK, (day - lastDay) * GRIFT_KEYS_PER_DAY);
  return { keys: Math.min(GRIFT_KEY_STOCK, Math.max(0, keys) + add), day };
}

function clampTier(t: number): number { return Math.max(1, Math.min(GRIFT_MAX, Math.floor(t) || 1)); }
